import React, { useEffect, useState } from 'react';
import apiClient from '../api/client';
import './EmployeeDocuments.css';

type Category = { id: string; name: string; internalOnly: boolean };
type Doc = {
  id: string;
  categoryId: string;
  category: Category;
  displayName: string;
  mimeType: string;
  size: number;
  issueDate?: string;
  expirationDate?: string;
  notes?: string;
  status: string;
  visibleToEmployee: boolean;
  validity: string;
  uploadedAt: string;
};
type Summary = { pending: number; expired: number; current: number; expiringSoon: number };

export default function EmployeeDocuments({
  employeeId,
  onBusy,
  onError,
}: {
  employeeId: string;
  onBusy: (x: string) => void;
  onError: (x: string) => void;
}) {
  const [items, setItems] = useState<Doc[]>([]),
    [categories, setCategories] = useState<Category[]>([]),
    [summary, setSummary] = useState<Summary>({
      pending: 0,
      expired: 0,
      current: 0,
      expiringSoon: 0,
    });
  const [filters, setFilters] = useState({ categoryId: '', status: '', expiration: '' }),
    [files, setFiles] = useState<File[]>([]),
    [form, setForm] = useState({
      categoryId: '',
      displayName: '',
      issueDate: '',
      expirationDate: '',
      notes: '',
      visibleToEmployee: true,
    }),
    [editing, setEditing] = useState<Doc | null>(null);
  const load = async () => {
    try {
      const [d, c, s] = await Promise.all([
        apiClient.get(`/employee-documents/employee/${employeeId}`, {
          params: Object.fromEntries(Object.entries(filters).filter(([, v]) => v)),
        }),
        apiClient.get('/employee-documents/categories'),
        apiClient.get(`/employee-documents/employee/${employeeId}/summary`),
      ]);
      setItems(d.data);
      setCategories(c.data);
      setSummary(s.data);
      if (!form.categoryId && c.data[0]) setForm((x) => ({ ...x, categoryId: c.data[0].id }));
    } catch {
      onError('No se pudo cargar la documentación del legajo.');
    }
  };
  useEffect(() => {
    void load();
  }, [employeeId, filters.categoryId, filters.status, filters.expiration]);
  const upload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!files.length) return onError('Seleccioná al menos un archivo.');
    onBusy('Subiendo documentación…');
    try {
      const data = new FormData();
      data.append('employeeId', employeeId);
      Object.entries(form).forEach(([k, v]) => {
        if (v !== '' && v !== undefined) data.append(k, String(v));
      });
      files.forEach((f) => data.append('files', f));
      await apiClient.post('/employee-documents', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setFiles([]);
      setForm((x) => ({ ...x, displayName: '', issueDate: '', expirationDate: '', notes: '' }));
      await load();
    } catch (e: any) {
      onError(e.response?.data?.message || 'No se pudieron cargar los archivos.');
    } finally {
      onBusy('');
    }
  };
  const open = async (d: Doc, download = false) => {
    onBusy(download ? 'Preparando descarga…' : 'Abriendo documento…');
    try {
      const r = await apiClient.get(`/employee-documents/${d.id}/file`, {
        params: { download: download ? '1' : '0' },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(r.data);
      if (download) {
        const a = document.createElement('a');
        a.href = url;
        a.download = d.displayName;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        window.open(url, '_blank', 'noopener,noreferrer');
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      }
    } catch {
      onError('No se pudo acceder al archivo.');
    } finally {
      onBusy('');
    }
  };
  const save = async () => {
    if (!editing) return;
    onBusy('Guardando metadatos…');
    try {
      await apiClient.patch(`/employee-documents/${editing.id}`, {
        categoryId: editing.categoryId,
        displayName: editing.displayName,
        issueDate: editing.issueDate || undefined,
        expirationDate: editing.expirationDate || undefined,
        notes: editing.notes || undefined,
        status: editing.status,
        visibleToEmployee: editing.visibleToEmployee,
      });
      setEditing(null);
      await load();
    } catch (e: any) {
      onError(e.response?.data?.message || 'No se pudo modificar el documento.');
    } finally {
      onBusy('');
    }
  };
  const remove = async (d: Doc) => {
    if (!confirm(`¿Dar de baja el documento “${d.displayName}”?`)) return;
    onBusy('Procesando baja…');
    try {
      await apiClient.delete(`/employee-documents/${d.id}`);
      await load();
    } catch {
      onError('No se pudo dar de baja el documento.');
    } finally {
      onBusy('');
    }
  };
  return (
    <section className="documents">
      <div className="document-stats">
        <span>
          <strong>{summary.pending}</strong>Pendientes
        </span>
        <span className="bad">
          <strong>{summary.expired}</strong>Vencidos
        </span>
        <span className="good">
          <strong>{summary.current}</strong>Vigentes
        </span>
        <span className="warn">
          <strong>{summary.expiringSoon}</strong>Por vencer
        </span>
      </div>
      <form className="document-upload" onSubmit={upload}>
        <h3>Cargar documentos</h3>
        <label>
          Categoría
          <select
            required
            value={form.categoryId}
            onChange={(e) => setForm({ ...form, categoryId: e.target.value })}
          >
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
                {c.internalOnly ? ' (interno)' : ''}
              </option>
            ))}
          </select>
        </label>
        <label>
          Nombre visible
          <input
            value={form.displayName}
            onChange={(e) => setForm({ ...form, displayName: e.target.value })}
            placeholder="Opcional en carga individual"
          />
        </label>
        <label>
          Emisión
          <input
            type="date"
            value={form.issueDate}
            onChange={(e) => setForm({ ...form, issueDate: e.target.value })}
          />
        </label>
        <label>
          Vencimiento
          <input
            type="date"
            value={form.expirationDate}
            onChange={(e) => setForm({ ...form, expirationDate: e.target.value })}
          />
        </label>
        <label className="wide">
          Observaciones
          <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </label>
        <label className="check">
          <input
            type="checkbox"
            checked={form.visibleToEmployee}
            onChange={(e) => setForm({ ...form, visibleToEmployee: e.target.checked })}
          />{' '}
          Visible en el portal
        </label>
        <label className="file wide">
          <input
            required
            multiple
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
            onChange={(e) => setFiles(Array.from(e.target.files || []))}
          />
          {files.length
            ? `${files.length} archivo(s) seleccionado(s)`
            : 'Elegir uno o varios archivos'}
        </label>
        <button className="primary-button">Cargar</button>
      </form>
      <div className="document-filters">
        <select
          value={filters.categoryId}
          onChange={(e) => setFilters({ ...filters, categoryId: e.target.value })}
        >
          <option value="">Todas las categorías</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
        >
          <option value="">Todos los estados</option>
          <option value="ACTIVE">Activo</option>
          <option value="ARCHIVED">Archivado</option>
        </select>
        <select
          value={filters.expiration}
          onChange={(e) => setFilters({ ...filters, expiration: e.target.value })}
        >
          <option value="">Cualquier vencimiento</option>
          <option value="expired">Vencidos</option>
          <option value="soon">Próximos 30 días</option>
          <option value="current">Vigentes</option>
        </select>
      </div>
      <div className="document-list">
        {items.length === 0 ? (
          <p>No hay documentos para estos filtros.</p>
        ) : (
          items.map((d) => (
            <article key={d.id}>
              <div>
                <strong>{d.displayName}</strong>
                <small>
                  {d.category.name} · {(Number(d.size) / 1024).toFixed(0)} KB ·{' '}
                  {d.validity === 'EXPIRED'
                    ? 'Vencido'
                    : d.validity === 'EXPIRING_SOON'
                      ? 'Próximo a vencer'
                      : 'Vigente'}
                  {d.visibleToEmployee ? ' · Visible' : ' · Interno'}
                </small>
              </div>
              <div className="row-actions">
                <button className="secondary-button" onClick={() => void open(d)}>
                  Ver
                </button>
                <button className="secondary-button" onClick={() => void open(d, true)}>
                  Descargar
                </button>
                <button
                  className="secondary-button"
                  onClick={() =>
                    setEditing({
                      ...d,
                      issueDate: d.issueDate?.slice(0, 10),
                      expirationDate: d.expirationDate?.slice(0, 10),
                    })
                  }
                >
                  Editar
                </button>
                <button className="danger-button" onClick={() => void remove(d)}>
                  Baja
                </button>
              </div>
            </article>
          ))
        )}
      </div>
      {editing && (
        <div className="document-modal">
          <div>
            <h3>Modificar documento</h3>
            <label>
              Nombre
              <input
                value={editing.displayName}
                onChange={(e) => setEditing({ ...editing, displayName: e.target.value })}
              />
            </label>
            <label>
              Categoría
              <select
                value={editing.categoryId}
                onChange={(e) => setEditing({ ...editing, categoryId: e.target.value })}
              >
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Emisión
              <input
                type="date"
                value={editing.issueDate || ''}
                onChange={(e) => setEditing({ ...editing, issueDate: e.target.value })}
              />
            </label>
            <label>
              Vencimiento
              <input
                type="date"
                value={editing.expirationDate || ''}
                onChange={(e) => setEditing({ ...editing, expirationDate: e.target.value })}
              />
            </label>
            <label>
              Observaciones
              <textarea
                value={editing.notes || ''}
                onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
              />
            </label>
            <label className="check">
              <input
                type="checkbox"
                checked={editing.visibleToEmployee}
                onChange={(e) => setEditing({ ...editing, visibleToEmployee: e.target.checked })}
              />{' '}
              Visible en el portal
            </label>
            <div>
              <button className="secondary-button" onClick={() => setEditing(null)}>
                Cancelar
              </button>
              <button className="primary-button" onClick={() => void save()}>
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
